import { motion } from "framer-motion";
import { scrollToId } from "../../hooks/useLenis";
import { useLang } from "../../i18n/LanguageContext";
import { useAuth } from "../../lib/AuthContext";
import { CodeRain } from "./CodeRain";
import { ArrowRight, Play, Sparkles } from "lucide-react";

const LINE_EASE = [0.16, 1, 0.3, 1];

const Line = ({ children, delay = 0 }) => (
  <span className="reveal-mask">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: LINE_EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLang();
  const { user, setAuthOpen } = useAuth();
  const onStart = () => (user ? scrollToId("cockpit") : setAuthOpen(true));
  const STATS = [
    { value: "10+", label: t("hero.statTools") },
    { value: "24/7", label: t("hero.statAuto") },
    { value: "$10M", label: t("hero.statRevenue") },
  ];
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pt-24"
    >
      {/* Live Matrix code rain */}
      <CodeRain opacity={0.45} />
      {/* focus glow */}
      <div className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(circle_at_26%_50%,rgba(37,99,235,0.16),transparent_50%)]" />
      {/* volumetric top ray */}
      <div className="pointer-events-none absolute -top-40 left-1/2 z-0 h-[600px] w-[900px] -translate-x-1/2 bg-[radial-gradient(ellipse_at_top,rgba(37,99,235,0.18),transparent_60%)]" />

      <div className="relative z-10 mx-auto w-full max-w-[1500px] px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: LINE_EASE, delay: 0.15 }}
          className="glass-panel max-w-3xl rounded-3xl p-8 sm:p-12"
          data-testid="hero-window"
        >
          <div
            className="mb-8 inline-flex items-center gap-3 rounded-full border border-blue-500/25 bg-blue-500/10 px-4 py-2"
            data-testid="hero-badge"
          >
            <Sparkles size={13} className="text-blue-300" />
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-100">
              {t("hero.badge")}
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="font-display text-4xl font-800 leading-[0.98] tracking-tighter text-white sm:text-5xl lg:text-6xl"
          >
            <Line delay={0.35}>{t("hero.l1")}</Line>
            <Line delay={0.5}>
              <span className="text-white/90">{t("hero.l2a")}</span>{" "}
              <span className="text-glow bg-gradient-to-r from-blue-300 via-blue-500 to-[#CBD5E1] bg-clip-text text-transparent">
                {t("hero.l2b")}
              </span>
            </Line>
            <Line delay={0.65}>
              <span className="text-glow bg-gradient-to-r from-blue-400 to-[#CBD5E1] bg-clip-text text-transparent">
                {t("hero.l3")}
              </span>
            </Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
            className="mt-8 max-w-xl font-body text-base leading-relaxed text-[#CBD5E1] sm:text-lg"
            data-testid="hero-subtitle"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9 }}
            className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <button
              data-testid="hero-cta-start"
              onClick={onStart}
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-blue-600 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white cta-glow transition-all duration-300 hover:scale-[1.02] hover:bg-blue-500"
            >
              {t("hero.ctaStart")}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-tools"
              onClick={() => scrollToId("cockpit")}
              className="inline-flex items-center justify-center gap-3 rounded-full border border-blue-500/30 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-blue-100 transition-all duration-300 hover:border-blue-400 hover:bg-blue-500/10"
            >
              <Play size={14} />
              {t("hero.ctaTools")}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.9 }}
            className="mt-10 flex gap-8 border-t border-blue-500/15 pt-6"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <div className="font-display text-2xl font-800 tracking-tighter text-white">{s.value}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8AA0C0]">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      <div className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CBD5E1]">scroll</span>
        <div className="h-12 w-px bg-gradient-to-b from-blue-500/60 to-transparent" />
      </div>
    </section>
  );
};
